import { Auth } from "aws-amplify";
import LZString from "lz-string";
import Cookies from "universal-cookie";

import { paths } from "app/constants/paths";
import { getAuthCookieOptions } from "app/utils/auth";
import { getConfigs } from "app/utils/configs";

import type { HubCapsule } from "@aws-amplify/core";

const REDIRECT_COOKIE = "stacklet-return-uri";

/**
 * Stores the current URL in a cookie so the user can be redirected back
 * after authentication.
 *
 * The callback URI (redirect_uri) passed to Cognito must exactly match a
 * pre-registered URI in the user pool, so the full requested URI is
 * preserved in a cookie instead.
 *
 * Skips storing transient routes like `/onboarding` — during auth state
 * transitions the routing guard can briefly redirect there before account
 * data loads. Users who genuinely need onboarding will be routed there
 * automatically after login.
 */
export function storeRedirectReturnCookie() {
  const url = new URL(window.location.href);
  if (url.pathname.startsWith(paths.onboarding)) {
    return;
  }

  const cookies = new Cookies();
  cookies.set(REDIRECT_COOKIE, window.location.href, getAuthCookieOptions());
}

/** Returns the stored redirect URI, or `undefined` if none is set. */
function getRedirectReturnCookie() {
  const cookies = new Cookies();
  return cookies.get(REDIRECT_COOKIE);
}

/** Removes the stored redirect URI cookie. */
function clearRedirectReturnCookie() {
  const cookies = new Cookies();
  cookies.remove(REDIRECT_COOKIE, getAuthCookieOptions());
}

/**
 * Handles post-sign-in redirection by reading the stored redirect cookie
 * and navigating to the appropriate destination (docs, Redash, GraphQL
 * playground, or the originally requested URL).
 *
 * The cookie is cleared after the redirect target is resolved so it is
 * preserved if the redirect logic throws.
 */
export async function signInRedirectHandler(data: HubCapsule) {
  if (data.payload.event !== "signIn") {
    return;
  }

  const redirectCookieValue = getRedirectReturnCookie();
  if (!redirectCookieValue) {
    return;
  }

  let redirectURL: URL;
  try {
    redirectURL = new URL(redirectCookieValue);
  } catch {
    clearRedirectReturnCookie();
    return;
  }

  const redirectTarget = redirectURL.searchParams.get("redirect") || "";
  const redirectPath =
    (redirectURL.searchParams.get("redirectPath") || "") + redirectURL.hash;

  // Clear the cookie only after successfully determining the redirect target,
  // so it's preserved if the redirect logic throws.
  clearRedirectReturnCookie();

  const redirected = await dispatchRedirect(redirectTarget, redirectPath);
  // An unrecognized (e.g. stale or tampered) target is left as a no-op. The
  // no-target case, and a recognized target that couldn't actually redirect
  // (e.g. Superset isn't configured in this environment), both fall back to
  // a plain restore of the original URL.
  if (
    !redirected &&
    (redirectTarget === "" || KNOWN_REDIRECT_TARGETS.has(redirectTarget))
  ) {
    window.location.assign(redirectURL.href);
  }
}

const KNOWN_REDIRECT_TARGETS = new Set(["docs", "assetdb", "api", "superset"]);

/** Navigates to the service named by `target`, returning whether a redirect was performed. */
export async function dispatchRedirect(
  target: string,
  path: string,
): Promise<boolean> {
  switch (target) {
    case "docs":
      return openDocs(path);
    case "assetdb":
      return openRedash(path);
    case "api":
      await openGraphQLPlayground();
      return true;
    case "superset":
      return openSuperset(path);
    default:
      return false;
  }
}

/**
 * Navigates to the documentation site, optionally appending a path. Like
 * Superset, the docs URL is not configured in every environment, so this reports
 * whether it navigated rather than assuming that it did.
 */
export async function openDocs(path = ""): Promise<boolean> {
  const configs = await getConfigs();
  if (!configs.docs) {
    return false;
  }
  window.location.assign(configs.docs.url + path);
  return true;
}

/**
 * Navigates to the Redash instance, optionally appending a path.
 *
 * Every deployment names a Redash host, but `application.json` leaves the block
 * out rather than guessing, so this reports whether it navigated the same way
 * docs and Superset do.
 */
export async function openRedash(path = ""): Promise<boolean> {
  const configs = await getConfigs();
  if (!configs.redash) {
    return false;
  }
  window.location.assign(configs.redash.url + path);
  return true;
}

/**
 * Navigates to the Superset instance, optionally appending a path. Unlike
 * Redash, Superset isn't deployed in every environment, so this reports
 * whether it was configured rather than assuming it always is.
 */
export async function openSuperset(path = ""): Promise<boolean> {
  const configs = await getConfigs();
  if (configs.superset?.url) {
    window.location.assign(configs.superset.url + path);
    return true;
  }
  return false;
}

/** Navigates to the Apollo Studio sandbox with pre-configured auth headers. */
export async function openGraphQLPlayground() {
  const headers = {
    authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`,
  };

  const encodedExplorerURLState = LZString.compressToEncodedURIComponent(
    JSON.stringify({
      headers: JSON.stringify(headers),
    }),
  );

  const configs = await getConfigs();

  const encodedConfig = encodeURIComponent(configs.api.url);

  const targetUri = `https://studio.apollographql.com/sandbox/explorer?endpoint=${encodedConfig}&explorerURLState=${encodedExplorerURLState}`;

  window.location.assign(targetUri);
}
